/**
 * Supabase Authentication Utilities - Client-Side Only
 *
 * Provides client-side auth helpers for React components.
 * Supports Google and Apple OAuth for social login.
 *
 * NOTE: For server-side auth, use the createServerSupabaseClient from supabase-auth-server.ts
 */

import { createBrowserClient } from '@supabase/ssr'
import type { User, Session, Provider } from '@supabase/supabase-js'

// Environment variables
const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!

/**
 * Create a Supabase client for use in the browser (Client Components)
 */
export function createClient() {
  return createBrowserClient(supabaseUrl, supabaseAnonKey)
}

/**
 * Sign in with OAuth provider (Google or Apple)
 * Returns the URL to redirect the user to for OAuth
 */
export async function signInWithOAuth(
  provider: 'google' | 'apple',
  redirectTo?: string
): Promise<{ url: string | null; error: Error | null }> {
  const supabase = createClient()

  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: redirectTo || `${window.location.origin}/auth/callback`,
      queryParams: provider === 'google' ? {
        access_type: 'offline',
        prompt: 'consent',
      } : undefined,
    },
  })

  return {
    url: data?.url || null,
    error: error ? new Error(error.message) : null,
  }
}

/**
 * Sign in with Google OAuth
 */
export async function signInWithGoogle(redirectTo?: string) {
  return signInWithOAuth('google', redirectTo)
}

/**
 * Sign in with Apple OAuth
 */
export async function signInWithApple(redirectTo?: string) {
  return signInWithOAuth('apple', redirectTo)
}

/**
 * Sign out the current user
 */
export async function signOut(): Promise<{ error: Error | null }> {
  const supabase = createClient()
  const { error } = await supabase.auth.signOut()
  return { error: error ? new Error(error.message) : null }
}

/**
 * Get the current session (client-side)
 */
export async function getSession(): Promise<Session | null> {
  const supabase = createClient()
  const { data: { session } } = await supabase.auth.getSession()
  return session
}

/**
 * Get the current user (client-side)
 */
export async function getUser(): Promise<User | null> {
  const supabase = createClient()
  const { data: { user } } = await supabase.auth.getUser()
  return user
}

/**
 * Subscribe to auth state changes (client-side)
 */
export function onAuthStateChange(
  callback: (event: string, session: Session | null) => void
) {
  const supabase = createClient()
  return supabase.auth.onAuthStateChange(callback)
}

/**
 * Refresh the current session
 */
export async function refreshSession(): Promise<Session | null> {
  const supabase = createClient()
  const { data: { session } } = await supabase.auth.refreshSession()
  return session
}

// Re-export types for convenience
export type { User, Session, Provider }
